/**
 * Resolving the operator's tenant from the token.
 *
 * 🔑 INVARIANT: the tenant comes from the TOKEN, never from the UI. No prop, no
 * dropdown, no localStorage, no query string. If the UI could choose it, the
 * backend would be comparing an attacker-chosen namespace against an
 * attacker-chosen namespace, and the equality check that protects a tenant
 * would pass for everyone.
 */

const METADATA_CLAIM = "urn:zitadel:iam:user:metadata";

export type Claims = Record<string, unknown>;

/**
 * ZITADEL emits user metadata values as UNPADDED base64.
 *
 * ⚠️ Scope note, so nobody mistakes this for the guard it is not: the estate's
 * historical bug was in GO, where base64.StdEncoding.DecodeString REJECTS
 * unpadded input and the error was dropped, leaving the ENCODED STRING to
 * become the namespace. JavaScript's atob implements *forgiving*-base64 per the
 * WHATWG spec, which already tolerates missing padding — so the explicit
 * re-padding below is defensive intent, NOT load-bearing here. Removing it does
 * not change behaviour in a browser or in Node, and no test can catch its
 * removal. Proven by mutation: deleting it left the suite fully green.
 *
 * 🪤 The failure atob DOES have is length % 4 === 1, which is a hard failure and
 * returns null — which is what makes the caller fail closed rather than proceed
 * with a bad namespace. That path is tested, because it can actually fail.
 */
export function decodeMetadataValue(value: string): string | null {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  try {
    const binary = atob(padded);
    // The values are ASCII namespace strings; decode as UTF-8 for safety.
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Two claim contracts, one token:
 *   radaccess (gateway) reads a FLAT top-level `namespace` string
 *   the Go backends read `urn:zitadel:iam:user:metadata`.namespace, BASE64
 * Read the flat claim first, then fall back to the metadata claim.
 */
export function resolveNamespace(claims: Claims | undefined | null): string | null {
  if (!claims) return null;

  const flat = claims["namespace"];
  if (typeof flat === "string" && flat.trim() !== "") return flat.trim();

  const metadata = claims[METADATA_CLAIM];
  if (metadata && typeof metadata === "object") {
    const encoded = (metadata as Record<string, unknown>)["namespace"];
    if (typeof encoded === "string" && encoded !== "") {
      const decoded = decodeMetadataValue(encoded);
      if (decoded && decoded.trim() !== "") return decoded.trim();
    }
  }
  return null;
}

/**
 * ZITADEL places roles under DIFFERENT claim keys for humans and machines:
 *   human with a project grant -> generic `urn:zitadel:iam:org:project:roles`
 *   machine user               -> project-QUALIFIED `...:project:<id>:roles`
 * AnkhOS is a human browser client, so the generic key is the one that
 * applies — but read both, because reading exactly one key is what decides
 * which callers work.
 */
export function resolveRoles(claims: Claims | undefined | null, projectId: string): string[] {
  if (!claims) return [];
  const keys = [
    "urn:zitadel:iam:org:project:roles",
    `urn:zitadel:iam:org:project:${projectId}:roles`,
  ];
  const out = new Set<string>();
  for (const k of keys) {
    const v = claims[k];
    if (v && typeof v === "object") {
      for (const role of Object.keys(v as Record<string, unknown>)) out.add(role);
    } else if (Array.isArray(v)) {
      for (const role of v) if (typeof role === "string") out.add(role);
    }
  }
  return [...out];
}

/**
 * The `urn:zitadel:` claim keys actually present on the token.
 *
 * 🔑 This exists to separate two failures that look identical from the UI:
 * a roles SCOPE that was dropped (no roles claim key at all) from a missing
 * role GRANT (the key is present and empty). Without it, an empty role list
 * sends you guessing between the client registration and the IdP's grants,
 * and those are owned by different people.
 *
 * An OAuth server drops scopes it does not recognise rather than failing, so
 * absence here is evidence about the request, not about the user.
 */
export function zitadelClaimKeys(claims: Claims | undefined | null): string[] {
  if (!claims) return [];
  return Object.keys(claims)
    .filter((k) => k.startsWith("urn:zitadel:"))
    .sort();
}

/**
 * The claims of the ACCESS token — the token this app actually sends.
 *
 * 🔑🔑 THE UI MUST READ ENTITLEMENT FROM THE TOKEN IT SENDS. `auth.user.profile`
 * is the ID TOKEN's claims, and the gateway is handed `auth.user.access_token`.
 * They are different tokens and ZITADEL does not populate them identically:
 * roles are asserted into the access token for the requested audience, and
 * reach the ID token only if the project is configured to assert them there.
 *
 * Reading roles from the ID token while sending the access token means the
 * screen can say "no role" about a token that carries one, or — worse in the
 * other direction — show an entitlement the backend will not honour. The two
 * must not be allowed to disagree, so entitlement is read from the access
 * token and the ID token is only a fallback.
 *
 * ⚠️ This DECODES, it does not VERIFY. That is correct here and must stay
 * understood: signature verification belongs to the backend, which does it on
 * every request. Nothing security-bearing may rest on this — it decides what to
 * DISPLAY and which request to bother sending, never what to permit.
 */
export function decodeAccessTokenClaims(token: string | null | undefined): Claims | null {
  if (!token) return null;
  const parts = token.split(".");
  // An opaque (non-JWT) access token is a legitimate configuration, not an
  // error — return null and let the caller fall back rather than throwing.
  if (parts.length !== 3) return null;
  const raw = parts[1];
  if (!raw) return null;
  const payload = raw.replace(/-/g, "+").replace(/_/g, "/");
  const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
  try {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as Claims;
  } catch {
    return null;
  }
}
